import {
    aplicarImagensNoHtml,
    extrairHtmlDaResposta,
    montarPromptDeSite,
    preencherTemplate,
    renderizarSite,
    templateDeFallback,
    valoresDoCliente,
    INSTRUCAO_SISTEMA_SITE,
    type DadosParaPromptDeSite,
    type RespostaIa,
} from "@nyroh/contracts";

import { Client } from "../types";
import { api, CDN_ASSETS, ErroApiNyroh } from "./api";

/**
 * ============================================================================
 * A IA SAIU DO NAVEGADOR — item B-07
 * ============================================================================
 *
 * O QUE ESTE ARQUIVO FAZIA ATE AGORA
 *
 *     const ai = new GoogleGenAI({ apiKey });
 *     await ai.models.generateContent({ ... })
 *
 * O NAVEGADOR chamava `generativelanguage.googleapis.com` direto, com uma chave
 * que o `vite.config.ts` inlinava no bundle. Dois problemas, e os dois
 * apareceram na pratica:
 *
 *  1. A chave ia escrita, em texto puro, no JavaScript entregue a qualquer
 *     visitante — e tambem no sourcemap publicado. Quem abrisse o DevTools
 *     queimava a cota, que e paga.
 *  2. Chave de API do Google NAO e aceita em requisicao de navegador para este
 *     endpoint: ele exige OAuth. O resultado observado foi
 *     `401 ACCESS_TOKEN_TYPE_UNSUPPORTED`, uma vez por item da esteira. A
 *     esteira nao produzia site nenhum, e o erro so aparecia no console.
 *
 * Agora quem fala com o Gemini e a API (`ia.gerar`), que ja tem a chave, ja
 * mede custo por cliente e ja sabe aplicar cota.
 *
 * ============================================================================
 * O QUE ESTA VERSAO TIROU DAQUI
 * ============================================================================
 *
 * 1. O PROMPT, que tinha 250 linhas neste arquivo. Ele foi para
 *    `@nyroh/contracts` (`prompt-site.ts`) porque o WORKER passou a gerar site
 *    tambem — e prompt em dois lugares nao diverge com erro, diverge com site
 *    diferente, e quem ajustou o texto de um lado jura que ajustou dos dois.
 *
 * 2. O REGISTRO DE CUSTO FALSO. Havia aqui um bloco que gravava
 *    `cost: 0.18` — um numero lido do localStorage — no localStorage e no banco,
 *    a cada geracao. O ramo que calcularia o custo por token estava logo acima,
 *    atras de `const usage = undefined`: codigo morto por construcao. A tela de
 *    custos somava esses R$ 0,18 e chamava de gasto. Agora quem registra e a
 *    API, com os tokens que o proprio Gemini reporta.
 *
 * E FALHA DE IA CONTINUA NAO SENDO FALHA DE ESTEIRA
 *
 * `generateSiteContent` cai para o TEMPLATE quando a IA nao responde. Isso e o
 * ponto: o caminho inteiro (spec -> render -> R2 -> borda) passa a ser
 * exercitavel sem cota, e a esteira sempre termina com um site publicavel.
 */

/** O `apiKey` das assinaturas antigas. Mantido e IGNORADO. */
type ChaveIgnorada = string | undefined;

/**
 * Teto das chamadas de IA, em milissegundos.
 *
 * 130 s, e nao os 30 s padrao do cliente. A API espera ate 100 s pelo provedor;
 * com o navegador desistindo aos 30 s acontecia o pior caso possivel: a fabrica
 * caia para o template achando que a IA nao respondeu, e a API terminava a
 * geracao, gastava os tokens e registrava o SUCESSO. Site de template, fatura de
 * IA, e nenhum erro em tela. Quem desiste primeiro tem de ser quem sabe o que
 * aconteceu — por isso o navegador espera mais que o servidor.
 */
const TETO_IA_MS = 130_000;

/**
 * Pede um HTML a IA pela API.
 *
 * Devolve `null` quando a IA nao esta disponivel (sem chave no servidor, cota
 * esgotada, provedor fora) — para quem chama decidir o que fazer. Erro de
 * programacao continua subindo: engolir tudo esconderia bug de contrato.
 */
async function pedirHtmlAIa(
    tarefa: 'gerar-site' | 'editar-site',
    partes: Array<{ tipo: 'texto'; texto: string } | { tipo: 'imagem'; mime: string; base64: string }>,
    clienteId?: string,
): Promise<RespostaIa | null> {
    try {
        return await api.chamar(
            'ia.gerar',
            { corpo: { tarefa, partes, origem: 'fabrica', ...(clienteId ? { clienteId } : {}) } },
            { timeoutMs: TETO_IA_MS },
        );
    } catch (e) {
        // 422 = a API diz que nao ha chave do Gemini; 502 = o provedor recusou;
        // 429 = a cota diaria do recurso `gemini` acabou.
        // Nos tres casos a resposta certa e o fallback, nao derrubar a esteira.
        if (e instanceof ErroApiNyroh && (e.status === 422 || e.status === 502 || e.status === 429)) {
            console.warn(`[ia] indisponivel (${e.status}): ${e.message}`);
            return null;
        }
        throw e;
    }
}

/**
 * O site pelo TEMPLATE, sem IA nenhuma.
 *
 * Deterministico: o mesmo cliente cai sempre no mesmo template e produz os
 * mesmos bytes — que e o que faz `content_hash` evitar republicar o identico.
 */
export function htmlPeloTemplate(client: Client): string {
    /*
      SORTEIA entre os cinco — mas semeado pelo id do cliente.

      O pedido era "escolher aleatoriamente entre os cinco", e o objetivo por
      tras dele e que cem clientes importados juntos nao saiam todos com a mesma
      cara. `templateDeFallback` atende isso: quando a categoria casa com um
      template (pizzaria -> Vitrine), respeita a categoria, porque isso e
      informacao de verdade; quando nao casa, sorteia em vez de mandar todo
      mundo para o mesmo padrao.

      `Math.random()` daria a mesma aparencia e quebraria duas coisas: a fila
      reprocessa job por desenho, e cada reprocesso publicaria um template
      diferente; e `content_hash` deixaria de evitar reescrita no R2, porque
      todo render seria diferente do anterior. Com a semente no id, o mesmo
      cliente cai sempre no mesmo template.

      Isto so roda no FALLBACK: os tres pontos que chamam esta funcao sao IA sem
      chave, IA sem HTML na resposta e falha inesperada. Com a IA respondendo,
      nada aqui e executado.
    */
    const template = templateDeFallback(client.id, client.industry, client.name);
    const spec = preencherTemplate(
        template,
        valoresDoCliente({
            nome: client.name,
            categoria: client.industry,
            telefone: client.leadData?.phoneNumber ?? null,
            email: client.email ?? null,
            endereco: client.leadData?.address ?? null,
            dadosNegocio: {
                resumo: client.leadData?.summary ?? null,
                palavrasChaveSeo: client.leadData?.seoKeywords ?? [],
            },
        }),
    );
    return renderizarSite(spec, { baseCdn: CDN_ASSETS });
}

/** Traduz o lead da fabrica para o que o prompt do contrato espera. */
function dadosDoCliente(client: Client, imagens: { locais: number; remotas: number; logo: boolean }): DadosParaPromptDeSite {
    const d = client.leadData;
    const servicos =
        (d?.serviceTags && d.serviceTags.length > 0 && d.serviceTags) ||
        (d?.seoKeywords && d.seoKeywords.length > 0 && d.seoKeywords) ||
        d?.categories ||
        [];

    return {
        nome: d?.name || client.name,
        categoria: client.industry,
        escopo: client.scope,
        telefone: d?.phoneNumber ?? null,
        endereco: d?.address ?? null,
        resumo: d?.summary ?? null,
        horarios: Array.isArray(d?.openingHours) ? d.openingHours.join(', ') : (d?.openingHours ?? null),
        servicos,
        nota: d?.rating ?? null,
        avaliacoes: (d?.reviews ?? []).map((r) => ({
            autor: r.author_name,
            texto: r.text,
            nota: r.rating ?? null,
        })),
        blueprint: (d?.blueprint ?? []).map((s) => ({
            titulo: s.title,
            estrategiaUx: s.uxStrategy,
            descricao: s.description,
        })),
        temImagens: imagens.logo || imagens.locais > 0 || imagens.remotas > 0,
        imagensLocais: imagens.locais,
        imagensRemotas: imagens.remotas,
        temLogo: imagens.logo,
    };
}

/** Separa `data:image/jpeg;base64,AAA` nas duas partes que o contrato pede. */
function partesDaImagem(dataUrl: string): { mime: string; base64: string } | null {
    const [meta, dados] = dataUrl.split(',');
    if (!meta || !dados) return null;
    return { mime: meta.match(/:(.*?);/)?.[1] ?? 'image/jpeg', base64: dados };
}

export const generateSiteContent = async (
    client: Client,
    /** Ignorado. A chave vive no servidor; ver o cabecalho deste arquivo. */
    _apiKey?: ChaveIgnorada,
): Promise<string> => {
    const d = client.leadData;

    // Ate 10 fotos. Cada uma custa token de entrada em TODA geracao do lote, e
    // acima disso o ganho de contexto nao paga o custo.
    const locais = (d?.localImages ?? []).slice(0, 10);
    const temLogo = Boolean(d?.avatar);
    const remotas = d?.remoteImages ?? [];

    const dados = dadosDoCliente(client, {
        locais: locais.length,
        remotas: remotas.length,
        logo: temLogo,
    });

    const partes: Array<
        { tipo: 'texto'; texto: string } | { tipo: 'imagem'; mime: string; base64: string }
    > = [{ tipo: 'texto', texto: montarPromptDeSite(dados) }];

    // A logo primeiro: o prompt fala dela como "IMAGEM 1".
    if (d?.avatar) {
        const p = partesDaImagem(d.avatar);
        if (p) partes.push({ tipo: 'imagem', ...p });
    }
    for (const img of locais) {
        const p = partesDaImagem(img);
        if (p) partes.push({ tipo: 'imagem', ...p });
    }

    try {
        const resposta = await pedirHtmlAIa('gerar-site', partes, client.id);

        // A IA nao respondeu (sem chave, cota, provedor fora). NAO e o fim da
        // esteira: o template cobre, e o site sai publicavel do mesmo jeito.
        if (!resposta) {
            console.warn(`[fabrica] "${client.name}": IA indisponivel, usando template.`);
            return htmlPeloTemplate(client);
        }

        const bruto = extrairHtmlDaResposta(resposta.html ?? resposta.texto ?? '');

        // Resposta vazia ou sem HTML tambem cai para o template — publicar uma
        // string vazia como site e pior que publicar o template.
        if (!bruto || !/<html[\s>]/i.test(bruto)) {
            console.warn(`[fabrica] "${client.name}": IA devolveu conteudo sem HTML, usando template.`);
            return htmlPeloTemplate(client);
        }

        /*
          O custo NAO e registrado aqui.

          A API ja gravou esta chamada em `uso_ia`, com os tokens que o proprio
          Gemini reportou, antes de responder. O bloco que existia neste ponto
          escrevia `cost: 0.18` — um numero de localStorage — no localStorage e no
          banco, e a tela de custos somava isso como se fosse medicao. Duas fontes
          para o mesmo fato, e a errada era a que a tela lia.
        */
        console.info(
            `[fabrica] "${client.name}": gerado por ${resposta.consumo.modelo} — ` +
                `${resposta.consumo.tokensTotal} tokens` +
                (resposta.consumo.custoBrl === null
                    ? ' (sem preco cadastrado para este modelo)'
                    : ` ≈ R$ ${resposta.consumo.custoBrl.toFixed(4)}`),
        );

        return aplicarImagensNoHtml(bruto, {
            logo: d?.avatar ?? null,
            locais,
            remotas,
        });
    } catch (error) {
        // Ultima rede: qualquer falha inesperada tambem cai para o template. A
        // esteira nunca termina sem site — era esse o comportamento antigo, e o
        // resultado foram 116 itens na fila e nenhuma pagina gerada.
        console.error(`[fabrica] "${client.name}": falha ao gerar com IA, usando template.`, error);
        return htmlPeloTemplate(client);
    }
};

export const fixSiteResponsiveness = async (
    currentHtml: string,
    /** Ignorado. A chave vive no servidor. */
    _apiKey?: ChaveIgnorada,
): Promise<string> => {

    const prompt = `
        TAREFA: Refatorar o HTML fornecido para corrigir problemas de responsividade.

        CONTEXTO: O código atual pode ter colunas que não empilham no celular, textos pequenos demais ou overflow horizontal.

        REGRAS RÍGIDAS:
        1. MANTENHA INTEGRO TODO O CONTEÚDO (Textos, Imagens, Links, IDs). Não mude o que está escrito.
        2. REESCREVA O CSS/CLASSES TAILWIND para garantir Mobile-First.
           - Onde tiver 'flex-row', mude para 'flex flex-col md:flex-row'.
           - Onde tiver 'grid-cols-X', mude para 'grid-cols-1 md:grid-cols-X'.
           - Remova larguras fixas ou absolutas que quebram o mobile.
        3. Garanta que imagens não vazem a tela (max-w-full h-auto).

        HTML ORIGINAL PARA CORRIGIR:
        ${currentHtml}

        SAÍDA: Retorne APENAS o código HTML corrigido. Sem explicações.
    `;

    const resposta = await pedirHtmlAIa('editar-site', [
        { tipo: 'texto', texto: prompt + "\n\n" + INSTRUCAO_SISTEMA_SITE },
    ]);

    // Sem IA nao ha como "corrigir responsividade": devolve o HTML como estava,
    // em vez de devolver vazio e apagar o site de quem clicou.
    if (!resposta) return currentHtml;
    return extrairHtmlDaResposta(resposta.html ?? resposta.texto ?? '') || currentHtml;
};

export const editSiteContent = async (
    currentHtml: string,
    instruction: string,
    /** Ignorado. A chave vive no servidor. */
    _apiKey?: ChaveIgnorada,
    imageUrls?: string[],
): Promise<string> => {

    // Se tiver imagens (array), adiciona ao contexto como lista de URLs
    if (imageUrls && imageUrls.length > 0) {
        instruction += `\n\nREFERÊNCIAS VISUAIS (IMAGENS): Use estas imagens se solicitado para substituir algo ou como referência de estilo/conteúdo. Se for pedido para adicionar galeria, use estas URLs:\n${imageUrls.join('\n')}`;
    }

    const prompt = `
        TAREFA: Editar o site existente seguindo estritamente as instruções do usuário.

        INSTRUÇÃO DO USUÁRIO: "${instruction}"

        REGRAS DE SEGURANÇA (CRÍTICO):
        1. Retorne O CÓDIGO HTML COMPLETO e VALIDADO. Não retorne apenas o trecho.
        2. NÃO REMOVA scripts essenciais (Tailwind CDN, Fontes, Scripts de proteção).
        3. Mantenha a estrutura geral, altere apenas o que foi pedido (texto, cor, seção, imagem).
        4. Se o usuário pedir para trocar imagens e forneceu URLs, use as URLs das imagens fornecidas nas tags <img> corretas.
        5. Mantenha a responsividade Mobile-First.
        6. IMPORTANTE: RETORNE APENAS O HTML. NÃO ESCREVA EXPLICAÇÕES.

        HTML ATUAL:
        ${currentHtml}
    `;

    const resposta = await pedirHtmlAIa('editar-site', [
        { tipo: 'texto', texto: prompt + "\n\n" + INSTRUCAO_SISTEMA_SITE },
    ]);

    // Sem IA, o editor devolve o que recebeu. Devolver vazio aqui apagaria o
    // site publicado no primeiro clique de "aplicar".
    if (!resposta) {
        throw new Error(
            'A IA nao esta disponivel agora. O site nao foi alterado. ' +
            'Confira a chave e a cota na tela "Inteligencia Artificial".',
        );
    }
    return extrairHtmlDaResposta(resposta.html ?? resposta.texto ?? '') || currentHtml;
};
